import { useIntl } from 'react-intl';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { useListFinancialDocuments } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import { FinancialDocument } from 'src/features/financialDocuments/utils/types';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import {
    FINANCIAL_DOCUMENT_PAYMENT_STATUS_BADGE_OPTIONS,
    FINANCIAL_DOCUMENT_STATUS_BADGE_OPTIONS,
    FINANCIAL_DOCUMENT_TYPE_BADGE_OPTIONS,
} from 'src/features/financialDocuments/utils/constants';
import CellFinancialDocumentActions from 'src/features/financialDocuments/components/tables/CellFinancialDocumentActions';
import { accountingHeadersMessages } from 'src/features/accountingHeaders/utils/messages';
import { useParams } from 'react-router-dom';
import CreateFinancialDocumentFormModal from 'src/features/financialDocuments/components/forms/CreateFinancialDocumentFormModal';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import CellDate from 'src/registry/components/table/cells/CellDate';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const FinancialDocumentsTable = () => {
    const { formatMessage, formatNumber } = useIntl();
    const { rangeId = '', customerId = '', subscriptionId = '' } = useParams();

    const permissionManageFinancialDocs = useHasPermissions([PERMISSION.MANAGE_FINANCIAL_DOCUMENTS]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionAccessFinancialDocs = useHasPermissions([PERMISSION.ACCESS_FINANCIAL_DOCUMENTS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();

    const {
        data: financialDocuments,
        isLoading,
        isError,
    } = useListFinancialDocuments({
        ...(rangeId && { accounting_range: rangeId }),
        ...(customerId && { customer: customerId }),
        ...(subscriptionId && { subscription: subscriptionId }),
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
    });

    const columnHelper = createColumnHelper<FinancialDocument>();

    const columns = [
        columnHelper.accessor('number', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/financial-documents/${ctx.row.original.id}`}
                        hasPermissions={permissionManageFinancialDocs}
                    />
                );
            },
            meta: {
                title: formatMessage(financialDocumentsMessages.refNumber),
            },
        }),
        columnHelper.accessor('customer.name', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                const customerId = ctx.row.original.customer?.id;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/customers/${customerId}`}
                        hasPermissions={permissionAccessCustomers}
                    />
                );
            },
            meta: {
                title: formatMessage(financialDocumentsMessages.financialDocCustomer),
            },
        }),
        columnHelper.accessor('financial_document.number', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                const financialDocumentId = ctx.row.original.financial_document?.id;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/financial-documents/${financialDocumentId}`}
                        hasPermissions={permissionAccessFinancialDocs}
                    />
                );
            },
            meta: {
                title: formatMessage(accountingHeadersMessages.parent),
            },
        }),
        columnHelper.accessor('issue_due_date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDate,
            meta: {
                title: formatMessage(financialDocumentsMessages.issueDate),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(commonMessages.type),
                inputType: INPUT_TYPE.MULTI_SELECT,
                badgeOptions: FINANCIAL_DOCUMENT_TYPE_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(generalMessages.status),
                inputType: INPUT_TYPE.MULTI_SELECT,
                badgeOptions: FINANCIAL_DOCUMENT_STATUS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('payment_status', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.paymentStatus),
                inputType: INPUT_TYPE.MULTI_SELECT,
                badgeOptions: FINANCIAL_DOCUMENT_PAYMENT_STATUS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('payment_due_date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDate,
            meta: {
                title: formatMessage(financialDocumentsMessages.paymentDueDate),
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return formatNumber(row.original.amount / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('amount_with_tax', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return formatNumber(row.original.amount_with_tax / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            meta: {
                title: formatMessage(financialDocumentsMessages.amountWithTax),
            },
        }),
        columnHelper.accessor('remaining_amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.remaining_amount
                    ? formatNumber(row.original.remaining_amount / 100, {
                          style: 'currency',
                          currency: row.original.currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(financialDocumentsMessages.remainingAmount),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellFinancialDocumentActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={financialDocuments}
            semantics={{
                title: formatMessage(financialDocumentsMessages.financialDocuments),
                entryName: formatMessage(financialDocumentsMessages.document).toLowerCase(),
                entryNames: formatMessage(financialDocumentsMessages.documents).toLowerCase(),
                placeholder: formatMessage(financialDocumentsMessages.document).toLowerCase(),
            }}
            search={search}
            pagination={pagination}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry:
                    permissionManageFinancialDocs && !subscriptionId ? CreateFinancialDocumentFormModal : undefined,
            }}
        />
    );
};

export default FinancialDocumentsTable;
